import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Card, CardContent, Chip, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { interviewService, InterviewQuestionsResponse, SkillQuestions } from '../../services/interviewService';
import { COLORS } from '../../config/colors';

interface InterviewQuestionsContentProps {
  candidate?: any;
  candidateId?: string;
  onContentChange?: (contentId: string) => void;
  onActionClick?: (action: string, data?: any) => void;
}

const InterviewQuestionsContent: React.FC<InterviewQuestionsContentProps> = ({ 
  candidate, 
  candidateId, 
  onContentChange, 
  onActionClick 
}) => {
  const [questionsData, setQuestionsData] = useState<InterviewQuestionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInterviewQuestions = async () => {
      // Get job_id and resume_id from candidate object
      const jobId = candidate?.job_id || candidate?.jobId;
      const resumeId = candidate?.resume_id || candidate?.id || candidateId;
      
      if (!jobId || !resumeId) {
        console.log('[InterviewQuestionsContent] Missing job_id or resume_id');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('[InterviewQuestionsContent] Fetching interview questions for:', { jobId, resumeId });
        const response = await interviewService.getInterviewQuestions(jobId, resumeId);
        
        setQuestionsData(response);
        console.log('[InterviewQuestionsContent] Interview questions fetched successfully:', response);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch interview questions';
        setError(errorMessage);
        console.error('[InterviewQuestionsContent] Error fetching interview questions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInterviewQuestions();
  }, [candidate, candidateId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)', mb: 2 }}>
        ❓ Interview Questions
      </Typography>
      <Typography sx={{ fontFamily: 'var(--font-body)', color: 'var(--dark-gray)', mb: 3 }}>
        Tailored interview questions based on job requirements and candidate profile.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {questionsData && (
        <Card sx={{ mb: 3, border: '1px solid #e0e7ff', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)' }}>
                📋 Interview Questions by Skill
              </Typography>
              {questionsData.total_questions && (
                <Chip 
                  label={`${questionsData.total_questions} Total Questions`} 
                  size="small"
                  sx={{ bgcolor: COLORS.INFO_LIGHT, color: 'var(--primary-blue)', fontWeight: 600 }}
                />
              )}
            </Box>

            {/* Metadata */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Job ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'var(--primary-blue)' }}>
                    {questionsData.job_id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Resume ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'var(--primary-blue)' }}>
                    {questionsData.resume_id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Skills Covered
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--primary-blue)', fontWeight: 600 }}>
                    {questionsData.skills?.length || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Skills and Questions */}
            {questionsData.skills && questionsData.skills.length > 0 ? (
              <Box>
                {questionsData.skills.map((skillData: SkillQuestions, skillIndex: number) => (
                  <Accordion 
                    key={skillIndex}
                    defaultExpanded={skillIndex === 0}
                    sx={{ 
                      mb: 2, 
                      border: '1px solid #e0e7ff',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      borderRadius: '8px !important',
                      overflow: 'hidden'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: '#f8f9fa',
                        '&:hover': { bgcolor: '#e9ecef' },
                        borderBottom: '1px solid #e0e7ff'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography variant="h6" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)', fontWeight: 600 }}>
                          {skillData.skill}
                        </Typography>
                        <Chip 
                          label={`${skillData.questions?.length || 0} Questions`} 
                          size="small"
                          sx={{ bgcolor: COLORS.INFO_LIGHT, color: 'var(--primary-blue)', fontWeight: 500 }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      {skillData.questions && skillData.questions.length > 0 ? (
                        <Box>
                          {skillData.questions.map((question, qIndex) => (
                            <Card 
                              key={qIndex}
                              sx={{ 
                                mb: 2, 
                                bgcolor: '#ffffff',
                                border: '1px solid #e0e7ff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                '&:hover': {
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                  borderColor: 'var(--primary-blue)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                  <Chip 
                                    label={`Q${qIndex + 1}`} 
                                    size="small"
                                    sx={{ 
                                      bgcolor: 'var(--primary-blue)', 
                                      color: 'white', 
                                      fontWeight: 700,
                                      minWidth: '45px'
                                    }}
                                  />
                                  <Box sx={{ flex: 1 }}>
                                    <Typography 
                                      variant="body1" 
                                      sx={{ 
                                        color: 'var(--dark-gray)', 
                                        lineHeight: 1.8,
                                        fontWeight: 500
                                      }}
                                    >
                                      {typeof question === 'string' ? question : question.question}
                                    </Typography>
                                    {typeof question === 'object' && (question.type || question.difficulty) && (
                                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        {question.type && (
                                          <Chip 
                                            label={question.type} 
                                            size="small"
                                            sx={{ 
                                              bgcolor: '#f3e5f5', 
                                              color: '#7b1fa2',
                                              fontSize: '0.75rem'
                                            }}
                                          />
                                        )}
                                        {question.difficulty && (
                                          <Chip 
                                            label={question.difficulty} 
                                            size="small"
                                            sx={{ 
                                              bgcolor: question.difficulty === 'Hard' ? COLORS.ERROR_LIGHT :
                                                       question.difficulty === 'Medium' ? COLORS.WARNING_LIGHT : COLORS.SUCCESS_LIGHT,
                                              color: question.difficulty === 'Hard' ? '#c62828' :
                                                     question.difficulty === 'Medium' ? COLORS. SECONDARY_RED : COLORS.SUCCESS_DARK,
                                              fontSize: '0.75rem',
                                              fontWeight: 600
                                            }}
                                          />
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontStyle: 'italic' }}>
                          No questions available for this skill.
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Alert severity="info">
                No interview questions available. Questions will be generated based on the job requirements and candidate profile.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InterviewQuestionsContent;

